import React from 'react';
import { TopBar } from './TopBar';
import { Version } from '../../ui/components/Version';

interface AppLayoutProps {
  children: React.ReactNode;
  className?: string;
  showTopBar?: boolean;
  fullWidth?: boolean;
  showFooter?: boolean;
}

export function AppLayout({ 
  children, 
  className = '', 
  showTopBar = true,
  fullWidth = false,
  showFooter = true
}: AppLayoutProps) {
  return (
    <div className="min-h-screen cutting-mat-bg-subtle flex flex-col">
      {showTopBar && <TopBar />}
      <main className={`flex-1 ${fullWidth ? '' : 'container mx-auto sm:px-6 lg:px-8'} ${className}`}>
        {children}
      </main>
      {showFooter && (
        <footer className="border-t-2 border-gray-800 bg-yellow-300 mt-auto">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Main Footer Content */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              {/* App Info Section */}
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <div className="bg-gray-800 border-2 border-gray-900 rounded-lg p-1 shadow-sm">
                    <svg 
                      className="w-4 h-4 text-yellow-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <span className="font-bold text-gray-800 text-lg">REFSTASH</span>
                </div>
                <p className="text-sm text-gray-700">
                  Collect. Organize. Inspire. Your UI reference stash with AI-powered features.
                </p>
              </div>

              {/* Quick Actions */}
              <div className="text-center">
                <h3 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Quick Actions</h3>
                <div className="space-y-2 max-w-xs mx-auto">
                  <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[32px] text-center">A</kbd>
                    <span className="flex-1 text-left">Add Reference</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[32px] text-center">H</kbd>
                    <span className="flex-1 text-left">Go Home</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[32px] text-center">/</kbd>
                    <span className="flex-1 text-left">Search</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[32px] text-center">T</kbd>
                    <span className="flex-1 text-left">Tag Cloud</span>
                  </div>
                   <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                     <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[32px] text-center">P</kbd>
                     <span className="flex-1 text-left">Prompts</span>
                   </div>
                   <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                     <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[32px] text-center">D</kbd>
                     <span className="flex-1 text-left">Directory</span>
                   </div>
                  <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <kbd className="px-2 py-1 bg-gray-800 text-yellow-300 border border-gray-900 rounded text-xs font-mono font-bold min-w-[40px] text-center">Esc</kbd>
                    <span className="flex-1 text-left">Back to Home</span>
                  </div>
                </div>
              </div>

              {/* Version & Links */}
              <div className="text-center md:text-right">
                <div className="mb-3">
                  <Version variant="footer" className="text-gray-700" />
                </div>
                <div className="flex items-center justify-center md:justify-end gap-4 text-sm">
                  <a 
                    href="/landing" 
                    className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
                  >
                    Features
                  </a>
                  <span className="text-gray-500">•</span>
                  <a 
                    href="/tag-cloud" 
                    className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
                  >
                    Tag Cloud
                  </a>
                  <span className="text-gray-500">•</span>
                   <a 
                     href="/saved-prompts" 
                     className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
                   >
                     Prompts
                   </a>
                   <span className="text-gray-500">•</span>
                   <a 
                     href="/public" 
                     className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
                   >
                     Directory
                   </a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-700 pt-4 text-center">
              <p className="text-xs text-gray-600">
                Made with <span className="text-red-600">♥</span> for designers and developers
              </p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
} 